/**
 * utils/timeframeHelpers.ts
 * Pure utility functions for Timeframe domain
 *
 * Provides functions to derive canonical value strings and UI labels
 * from timeframe category and interval. These are pure functions with
 * no side effects or external dependencies.
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Only `import type` from `types/` (no runtime dependencies)
 * - No side effects (no logs, no mutations, no I/O)
 * - All functions are deterministic and testable
 *
 * CANONICAL CASING:
 * - Lowercase: m (minutes), t (ticks)
 * - Uppercase: H (hours), D (days), W (weeks), M (months), R (range)
 */

import type { TimeframeCategory, TimeframeOption } from '@/types/schema/BasicSettings';

/**
 * Sort order for timeframe categories (deterministic display order)
 *
 * Categories are ordered from shortest to longest time period:
 * ticks -> minutes -> hours -> days -> weeks -> months -> range
 */
export const CATEGORY_SORT_ORDER: Record<TimeframeCategory, number> = {
  ticks: 0,
  minutes: 1,
  hours: 2,
  days: 3,
  weeks: 4,
  months: 5,
  range: 6
};

/**
 * Ordered array of timeframe categories (derived from CATEGORY_SORT_ORDER)
 *
 * Sorted by numeric priority to guarantee deterministic order.
 * Use this for iteration when category order matters.
 */
export const CATEGORY_ORDER: TimeframeCategory[] = (
  Object.entries(CATEGORY_SORT_ORDER) as [TimeframeCategory, number][]
)
  .sort((a, b) => a[1] - b[1])
  .map(([key]) => key);

/**
 * Sort timeframe options by category priority, then by interval ascending
 *
 * Provides deterministic ordering for display in dropdowns and lists.
 * Does not mutate the input array.
 *
 * @param options - Array of TimeframeOption objects
 * @returns New sorted array
 *
 * @example
 * ```ts
 * const options = [
 *   { id: '1', category: 'hours', interval: 4 },
 *   { id: '2', category: 'minutes', interval: 15 },
 *   { id: '3', category: 'minutes', interval: 5 },
 * ];
 * sortTimeframeOptions(options);
 * // => [
 * //   { id: '3', category: 'minutes', interval: 5 },
 * //   { id: '2', category: 'minutes', interval: 15 },
 * //   { id: '1', category: 'hours', interval: 4 },
 * // ]
 * ```
 */
export function sortTimeframeOptions(options: TimeframeOption[]): TimeframeOption[] {
  return [...options].sort((a, b) => {
    const categoryDiff = CATEGORY_SORT_ORDER[a.category] - CATEGORY_SORT_ORDER[b.category];
    if (categoryDiff !== 0) return categoryDiff;
    return a.interval - b.interval;
  });
}

/**
 * Get the canonical value string for a timeframe
 *
 * Returns a compact string representation suitable for persistence
 * and data transfer. Uses consistent casing per category.
 *
 * @param category - The timeframe category
 * @param interval - The numeric interval (e.g., 15 for 15m, 4 for 4H)
 * @returns Canonical value string
 *
 * @example
 * ```ts
 * getTimeframeValue('minutes', 15); // => '15m'
 * getTimeframeValue('hours', 4);    // => '4H'
 * getTimeframeValue('days', 1);     // => 'D'
 * getTimeframeValue('days', 2);     // => '2D'
 * getTimeframeValue('weeks', 1);    // => 'W'
 * getTimeframeValue('months', 1);   // => 'M'
 * getTimeframeValue('ticks', 100);  // => '100t'
 * getTimeframeValue('range', 500);  // => '500R'
 * ```
 */
export function getTimeframeValue(category: TimeframeCategory, interval: number): string {
  switch (category) {
    case 'minutes':
      return `${interval}m`;
    case 'hours':
      return `${interval}H`;
    case 'days':
      return interval === 1 ? 'D' : `${interval}D`;
    case 'weeks':
      return interval === 1 ? 'W' : `${interval}W`;
    case 'months':
      return interval === 1 ? 'M' : `${interval}M`;
    case 'ticks':
      return `${interval}t`;
    case 'range':
      return `${interval}R`;
  }
}

/**
 * Get the UI display label for a timeframe
 *
 * Returns a human-readable string suitable for displaying in
 * dropdowns, lists, and other UI elements.
 *
 * @param category - The timeframe category
 * @param interval - The numeric interval (e.g., 15 for 15 Minutes, 4 for 4 Hours)
 * @returns Human-readable label
 *
 * @example
 * ```ts
 * getTimeframeLabel('minutes', 1);  // => '1 Minute'
 * getTimeframeLabel('minutes', 15); // => '15 Minutes'
 * getTimeframeLabel('hours', 1);    // => '1 Hour'
 * getTimeframeLabel('hours', 4);    // => '4 Hours'
 * getTimeframeLabel('days', 1);     // => 'Daily'
 * getTimeframeLabel('days', 2);     // => '2 Days'
 * getTimeframeLabel('weeks', 1);    // => 'Weekly'
 * getTimeframeLabel('months', 1);   // => 'Monthly'
 * getTimeframeLabel('ticks', 1);    // => '1 Tick'
 * getTimeframeLabel('ticks', 100);  // => '100 Ticks'
 * getTimeframeLabel('range', 500);  // => '500 Range'
 * ```
 */
export function getTimeframeLabel(category: TimeframeCategory, interval: number): string {
  switch (category) {
    case 'minutes':
      return interval === 1 ? '1 Minute' : `${interval} Minutes`;
    case 'hours':
      return interval === 1 ? '1 Hour' : `${interval} Hours`;
    case 'days':
      return interval === 1 ? 'Daily' : `${interval} Days`;
    case 'weeks':
      return interval === 1 ? 'Weekly' : `${interval} Weeks`;
    case 'months':
      return interval === 1 ? 'Monthly' : `${interval} Months`;
    case 'ticks':
      return interval === 1 ? '1 Tick' : `${interval} Ticks`;
    case 'range':
      return interval === 1 ? '1 Range' : `${interval} Range`;
  }
}
